// What the app knows, in a form something outside the WebView can read.
//
// A widget, a watch face, a Siri intent and a Live Activity all run in their
// own process. None of them can open `localStorage`, IndexedDB or the Vue
// store, and none of them can ask the app a question — the app is not running.
// So the app leaves them a SNAPSHOT: a small, versioned object written into a
// container both processes can see (an App Group on iOS, SharedPreferences on
// Android), rewritten whenever the state behind it changes.
//
// TWO FACTS AND NOTHING ELSE. A surface outside the app needs to know what
// this person cares about (which city, which station, which lines, where home
// and work are, and which trips they described), and how to ask for departures
// (the API base). Everything else — the ledger, the disruptions, the quiet
// hours, the cached boards — belongs to the app and stays there. A snapshot
// that grows into a mirror of the store is a second source of truth that goes
// stale in a process nobody is watching.
//
// THE TRIPS ARE CARRIED AS SCHEDULES, NOT AS A VERDICT. It would be smaller to
// write "the trip that is on right now" and let the widget draw it — and it
// would be wrong within the hour. The app is not running when the 09:30 passes;
// nothing would rewrite the answer, and the widget would go on claiming the
// morning commute until somebody opened the app. So the schedule travels and
// the WIDGET decides, on its own clock, every time it redraws.
//
// PRIVACY. `home` and `work` are a stop id, a station name and a walking
// radius, and they never leave the device: this file's output goes to the
// shared container on the SAME phone and to nowhere else. There is no request
// in this module and no request in the native writers behind it. The server is
// never told where anybody lives — see the note at the top of `src/places.js`,
// which is the same promise made one layer down.
//
// A TRIP IS TWO STATIONS AND A CLOCK, and that is the whole of it here. The
// resolved `path` — every stop and route between the two ends — stays in the
// app: the widget draws an origin, a destination and a window, and nothing it
// draws needs the middle. A rule that is switched off does not travel at all,
// because nothing outside the app has a use for it.
//
// PURE. It is handed the state and returns the object. Reading the store,
// deciding when to write and carrying the bytes across the bridge are
// `src/snapshot-bridge.js`'s job, which is what makes this testable without a
// phone attached.

/**
 * The shape's version.
 *
 * Read by the native side before anything else: a widget compiled against v1
 * that is handed a v2 snapshot shows its "open the app" state rather than
 * guessing at fields it does not know. Bump it when a field changes meaning or
 * leaves; adding an optional field does not need a bump, because a reader that
 * does not know about it ignores it.
 *
 * `trips` DID NOT BUMP IT, and the reasoning is the whole of the compatibility
 * story for this feature. There are three native readers — the iPhone widget,
 * the Android widget and the watch complication — and every one of them checks
 * `version == 1` for EQUALITY, so a 2 here would blank all three on every
 * phone until its owner happened to install a build with the new Swift in it.
 * What travels instead is one optional field:
 *
 *   an OLD reader handed a NEW snapshot ignores a key it does not decode, and
 *   goes on showing the station board, which is exactly the fallback;
 *   a NEW reader handed an OLD snapshot finds no `trips`, reads it as none,
 *   and goes on showing the station board, which is the same fallback.
 *
 * Neither is a failure state, and neither needs the two sides to move together.
 * The day a field CHANGES MEANING or leaves is the day this becomes a 2.
 */
export const SNAPSHOT_VERSION = 1;

/**
 * The keys version 1 shipped with, and which may never quietly leave it.
 *
 * Separate from the list below so that the test can say the two different
 * things it means: that nothing new joins the payload unnoticed (`SNAPSHOT_KEYS`)
 * and that nothing OLD falls out of it while the version still says 1, which
 * is the half that would break a reader already on a phone.
 */
export const SNAPSHOT_V1_KEYS = Object.freeze([
  'version',
  'updatedAt',
  'city',
  'apiBase',
  'station',
  'stations',
  'lines',
  'home',
  'work',
]);

/**
 * Every key a snapshot may carry, and the whole of it.
 *
 * Exported because the test asserts against this list rather than against a
 * copy of it — the point of the assertion is that nothing quietly joins the
 * payload, and a test carrying its own list would stop noticing the day
 * somebody updated both.
 */
export const SNAPSHOT_KEYS = Object.freeze([...SNAPSHOT_V1_KEYS, 'trips']);

/** How many saved stations travel. Five is the whole of anybody's commute. */
export const MAX_STATIONS = 5;

/**
 * How many trips travel.
 *
 * The app allows twenty rules, which is a list screen's worth. A widget picks
 * ONE of them and never shows the rest, so what has to arrive is enough to
 * cover a week of described commutes and not the whole editor: six is a there
 * and back on weekdays plus a weekend pair, and the cap keeps a shared file
 * that three processes parse on every redraw from growing without a ceiling.
 */
export const MAX_TRIPS = 6;

/** A stop id that is actually a stop id, or 0. */
function stopId(value) {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

/** A station as the shared container holds it: an id and a name, no more. */
function station(row) {
  const id = stopId(row?.stopId);
  if (!id) return null;
  const name = String(row?.name ?? '').trim();
  if (!name) return null;
  return { stopId: id, name };
}

/**
 * A place: a station and the walk somebody will do from it.
 *
 * Half a place is no place. A slot with a stop id the bundle could not name —
 * a station saved in Melbourne and read while Sydney is showing — comes back
 * null, which is the same answer every other reader of `places` gives it.
 */
function place(row) {
  const base = station(row);
  if (!base) return null;
  const walkMinutes = Number(row?.walkMinutes);
  if (!Number.isFinite(walkMinutes) || walkMinutes <= 0) return null;
  return { ...base, walkMinutes };
}

/** 'HH:MM' as itself, or null when it is not that. */
function clock(value) {
  const m = /^(\d{1,2}):(\d{2})$/.exec(String(value ?? '').trim());
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (h > 23 || min > 59) return null;
  return `${String(h).padStart(2, '0')}:${m[2]}`;
}

/** Minutes past midnight for an already-validated 'HH:MM'. */
const minutesOf = (hhmm) => Number(hhmm.slice(0, 2)) * 60 + Number(hhmm.slice(3));

/**
 * A commute rule as the shared container holds it: two ends and a window.
 *
 * WHAT IS DROPPED IS AS DELIBERATE AS WHAT IS KEPT. `path` is the resolved
 * stops and routes between the two ends and no reader outside the app has a
 * use for it. `active` is gone because an inactive rule is not written at all
 * — a field carrying "ignore me" is a field somebody eventually forgets to
 * check. `mutedUntil` DOES travel, because a mute can expire while the app is
 * closed and the widget has to be able to come back on its own.
 *
 * Half a trip is no trip. A rule with one end missing, with a window that does
 * not parse, or with an end that is not after its start — which `activeAt`
 * never matches either — comes back null rather than half-drawn.
 */
function trip(row) {
  if (!row || row.active === false) return null;

  const from = stopId(row.from);
  const to = stopId(row.to);
  if (!from || !to || from === to) return null;

  const fromName = String(row.fromName ?? '').trim();
  const toName = String(row.toName ?? '').trim();
  if (!fromName || !toName) return null;

  const start = clock(row.start);
  const end = clock(row.end);
  if (!start || !end || minutesOf(end) <= minutesOf(start)) return null;

  const lead = Number(row.lead);
  const muted = Date.parse(row.mutedUntil ?? '');

  return {
    id: String(row.id ?? ''),
    from,
    fromName,
    to,
    toName,
    start,
    end,
    // `getDay()` order, 0 for Sunday, exactly as `src/rules.js` stores it —
    // and the native readers count their weekdays the same way for the same
    // reason. An empty list means every day, there as here.
    days: [...new Set((Array.isArray(row.days) ? row.days : [])
      .map(Number)
      .filter((d) => Number.isInteger(d) && d >= 0 && d <= 6))].sort((a, b) => a - b),
    lead: Number.isFinite(lead) ? Math.max(0, Math.round(lead)) : 0,
    mutedUntil: Number.isFinite(muted) ? new Date(muted).toISOString() : null,
  };
}

/**
 * The snapshot for a state.
 *
 * Everything is normalised on the way in rather than trusted: this object is
 * decoded by a Swift struct and a Kotlin data class, and a `stopId` that
 * arrived as the string "1034" is a decode failure on one platform and a
 * silently wrong URL on the other.
 *
 * @param {object} state
 * @param {string} state.city            the city in force, e.g. 'melbourne'
 * @param {string} state.apiBase         absolute origin the API is served from
 * @param {object} state.station         the board's station right now
 * @param {object[]} state.stations      the saved stations, in their own order
 * @param {number[]} state.lines         watched route ids
 * @param {object} state.home            { stopId, name, walkMinutes } or null
 * @param {object} state.work            { stopId, name, walkMinutes } or null
 * @param {object[]} state.rules         the commute rules of the city in force
 * @param {number} [state.now]           the clock, for the tests
 */
export function buildSnapshot({
  city,
  apiBase,
  station: current = null,
  stations = [],
  lines = [],
  home = null,
  work = null,
  rules = [],
  now = Date.now(),
} = {}) {
  const saved = (Array.isArray(stations) ? stations : [])
    .map(station)
    .filter(Boolean)
    // Deduplicated on stop id, because the saved list and the station the board
    // happens to be showing are two views of the same thing and a station that
    // is both would otherwise appear twice in a four-row widget.
    .filter((s, i, all) => all.findIndex((o) => o.stopId === s.stopId) === i)
    .slice(0, MAX_STATIONS);

  return {
    version: SNAPSHOT_VERSION,
    // WHEN THE APP LAST LOOKED, not when the departures were read. A widget
    // whose snapshot is a fortnight old is a widget on a phone whose owner has
    // not opened the app since they changed jobs, and the honest thing for it
    // to say is which station it is still watching and when it was told.
    updatedAt: new Date(now).toISOString(),
    city: String(city ?? '').trim() || null,
    // No trailing slash, ever. The native clients append '/api/departures…'
    // and a doubled slash is a 404 on a server that is otherwise fine.
    apiBase: String(apiBase ?? '').trim().replace(/\/+$/, '') || null,
    station: station(current) ?? saved[0] ?? null,
    stations: saved,
    // IDS, not records. Names and colours are DATA about the network and live
    // in the generated line table both native sides already carry — see
    // `scripts/build-line-table.mjs`. Putting them here too would be the same
    // fact in two places, and the copy in the shared container is the one that
    // would still say "Frankston is green" after the bundle said otherwise.
    lines: [...new Set((Array.isArray(lines) ? lines : [])
      .map(Number)
      .filter((n) => Number.isFinite(n) && n > 0))],
    home: place(home),
    work: place(work),
    // IN THE ORDER THE PERSON WROTE THEM, which is the order the app lists
    // them in and the tie-break the widget falls back on when two windows
    // overlap. Sorting them by time here would make that tie-break a thing
    // nobody chose.
    trips: (Array.isArray(rules) ? rules : []).map(trip).filter(Boolean).slice(0, MAX_TRIPS),
  };
}

/**
 * Is this snapshot different from that one, ignoring the clock?
 *
 * The writer calls this before crossing the bridge. Every write wakes the
 * widget timeline, and a timestamp that ticks on every keystroke would be a
 * reload budget spent on nothing — so the stamp is deliberately not part of
 * the comparison.
 */
export function snapshotChanged(a, b) {
  if (!a || !b) return a !== b;
  const strip = ({ updatedAt, ...rest }) => rest;   // eslint-disable-line no-unused-vars
  return JSON.stringify(strip(a)) !== JSON.stringify(strip(b));
}
